import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Clock } from "lucide-react";
import { es as esLocale } from "date-fns/locale";
import { Calendar } from "../ui/calendar";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const fmtTime = (t) => { const [h, m] = t.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export { fmtTime };

export default function SlotPicker({ hold, onHeld, hours = 0, compact = false }) {
  const { t, lang } = useLang();
  const [days, setDays] = useState(null);
  const [day, setDay] = useState(hold?.date || null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    const start = toIso(new Date(Date.now() + 86400000));
    api.get(`/availability?start=${start}&days=42&hours=${hours || 0}`).then((r) => {
      const open = r.data.filter((d) => d.slots.some((s) => s.available > 0));
      setDays(open);
      if (!day && open[0]) setDay(open[0].date);
    }).catch((e) => toast.error(errMsg(e)));
  }, [hours]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = async (time) => {
    setBusy(time);
    try {
      const r = await api.post("/holds", { date: day, time, hours: hours || 0, previous_hold_id: hold?.hold_id || null });
      onHeld(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!days) return <Loader2 className="animate-spin text-sky-600" />;
  const openSet = new Set(days.map((d) => d.date));
  const current = days.find((d) => d.date === day);
  const selected = day ? new Date(`${day}T12:00:00`) : undefined;
  return (
    <div className={`grid gap-6 ${compact ? "" : "md:grid-cols-2"}`} data-testid="slot-picker">
      <div>
        <p className="label">{t.book.pickDate}</p>
        <div className="rounded-2xl border border-slate-200 inline-block" data-testid="booking-calendar">
          <Calendar mode="single" selected={selected} defaultMonth={selected} locale={lang === "es" ? esLocale : undefined}
            onSelect={(d) => d && setDay(toIso(d))} disabled={(d) => !openSet.has(toIso(d))}
            fromDate={new Date()} toDate={days.length ? new Date(`${days[days.length - 1].date}T12:00:00`) : undefined} />
        </div>
        <p className="text-xs text-slate-500 mt-2">{t.book.calendarHint}</p>
      </div>
      <div>
        <p className="label">{t.book.pickTime}{day && <span className="text-slate-500 font-normal"> · {new Date(`${day}T12:00:00`).toLocaleDateString(lang === "es" ? "es-US" : "en-US", { weekday: "long", month: "long", day: "numeric" })}</span>}</p>
        {!current?.slots.length ? <p className="text-sm text-slate-500">{t.book.noSlots}</p> : (
          <div className={`grid gap-3 ${compact ? "grid-cols-3" : "grid-cols-2"}`}>
            {current.slots.map((s) => {
              const active = hold?.date === day && hold?.time === s.time;
              return (
                <button key={s.time} type="button" disabled={s.available <= 0 && !active} onClick={() => pick(s.time)} data-testid={`time-option-${s.time}`}
                  className={`px-4 py-3 rounded-xl border text-sm font-semibold flex items-center justify-between transition-colors disabled:opacity-40 ${active ? "border-sky-600 bg-sky-600 text-white" : "border-slate-200 hover:border-sky-400"}`}>
                  {busy === s.time ? <Loader2 size={16} className="animate-spin" /> : fmtTime(s.time)}
                  <span className={`text-xs font-normal ${active ? "text-sky-100" : "text-slate-500"}`}>{s.available} {t.book.left}</span>
                </button>
              );
            })}
          </div>
        )}
        {hold && (
          <p className="mt-4 text-sm text-emerald-700 flex items-center gap-2" data-testid="hold-confirmation"><Clock size={15} />{t.book.held} {new Date(hold.expires_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>
        )}
      </div>
    </div>
  );
}
